import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { HealthState } from "contracts/enums/core";
import { HEALTH_STATE } from "contracts/enums/core";
import type { SummaryQuickLook } from "../../enums/scan";
import { pluralize } from "../../ui/format";
import { overviewScanAvailability } from "./scanAvailability";
import type { ScanCoverage } from "./scanCoverage";
import {
  contentFitnessHeadline,
  contentFitnessPageCoverage,
  estateStats,
} from "./scanModel";

export {
  establishedLinkedEvidenceCount,
  populatedGuidanceRows,
  sourceCountLabel,
  sourceCoverage,
  sourceTrustStatus,
  traceabilityStatus,
  versionStatusLabel,
} from "./summaryGuidance";

export function HeroMetric(props: { label: string; value: string }) {
  return (
    <div class="signal-hero-metric">
      <strong>{props.value}</strong>
      <span>{props.label}</span>
    </div>
  );
}

export function Info(props: { text: string }) {
  return (
    <button class="signal-info" type="button" aria-label={props.text}>
      i
    </button>
  );
}

export function isSummaryQuickLook(
  value: string | null,
): value is SummaryQuickLook {
  return value === "fitness" || value === "brand-guidelines";
}

export function executiveVerdict(data: OverviewResponse): string {
  const score = contentFitnessHeadline(data).score;
  if (score === null) {
    return overviewScanAvailability(data).label;
  }
  if (score >= 80) {
    return "Strong estate, with a few important leaks.";
  }
  if (score >= 65) {
    return "Healthy base. Priority pages still need clearer paths.";
  }
  return "Content structure is holding the estate back.";
}

export function verdictBody(
  data: OverviewResponse,
  scanCoverage: ScanCoverage | null = null,
): string {
  const stats = estateStats(data);
  const reportCoverage = contentFitnessPageCoverage(data);
  if (contentFitnessHeadline(data).score === null) {
    return overviewScanAvailability(data).detail;
  }
  const inventoryPages = scanCoverage?.inventoryPages ?? data.pages.length;
  const completedPages = scanCoverage?.completedPages;
  const completionContext =
    completedPages !== null && completedPages !== undefined
      ? `${completedPages} of ${inventoryPages} inventory pages completed; `
      : "";
  const failureContext =
    (scanCoverage?.failedPages ?? 0) > 0
      ? ` Faro could not complete ${scanCoverage?.failedPages} ${pluralize(scanCoverage?.failedPages ?? 0, "page", "pages")} in this run.`
      : "";
  return `${completionContext}${reportCoverage.scoredPages} pages produced a Content Fitness score and ${reportCoverage.assessedPages} were assessed.${failureContext} The site has ${stats.weakPageCount} ${pluralize(stats.weakPageCount, "page", "pages")} needing attention and ${stats.brokenTransitionCount} recorded weak structural ${pluralize(stats.brokenTransitionCount, "connection", "connections")}. Start with the top findings, then inspect the affected pages and site structure.`;
}

export function scoreBand(score: number | null): HealthState {
  if (score === null) {
    return HEALTH_STATE.NODATA;
  }
  if (score >= 75) {
    return HEALTH_STATE.GOOD;
  }
  if (score >= 55) {
    return HEALTH_STATE.WATCH;
  }
  return HEALTH_STATE.POOR;
}

export function scoreBandLabel(score: number | null): string {
  switch (scoreBand(score)) {
    case HEALTH_STATE.GOOD:
      return "Strong";
    case HEALTH_STATE.WATCH:
      return "Needs attention";
    case HEALTH_STATE.POOR:
      return "High priority";
    default:
      return "No scored data";
  }
}

// Explains an empty Content Fitness section.
export function fitnessEmptyCopy(): string {
  return "No Content Fitness dimensions were scored in this scan yet.";
}

export function summaryScoreDelta(value: number | null): string {
  if (value === null) {
    return "";
  }
  if (value === 0) {
    return "No change";
  }
  return `${value > 0 ? "+" : ""}${value} ${pluralize(Math.abs(value), "point", "points")}`;
}
